/** `quotes_strip` precedes `quote` so `{{quotes_strip:id}}` is not parsed as a quote. */
export const ARTIFACT_EMBED_KINDS = ['plot', 'quotes_strip', 'quote'] as const

export type ArtifactEmbedKind = (typeof ARTIFACT_EMBED_KINDS)[number]

export interface ArtifactTextSegment {
  kind: 'text'
  value: string
}

export interface ArtifactEmbedSegment {
  kind: ArtifactEmbedKind
  id: string
}

export type ArtifactSegment = ArtifactTextSegment | ArtifactEmbedSegment

export interface ArtifactEditorNode {
  type: string
  attrs?: Record<string, unknown>
  text?: string
  marks?: Array<{ type?: string }>
  content?: Array<ArtifactEditorNode>
}

export interface ArtifactEditorDoc {
  type: 'doc'
  content?: Array<ArtifactEditorNode>
}

const EMBED_KIND_ALT = ARTIFACT_EMBED_KINDS.join('|')
const EMBED_TOKEN_RE = new RegExp(
  `\\{\\{(${EMBED_KIND_ALT}):([A-Za-z0-9][A-Za-z0-9._:-]*)\\}\\}`,
  'g',
)

const EMBED_NODE_TYPE: Record<ArtifactEmbedKind, string> = {
  plot: 'plotEmbed',
  quote: 'quoteEmbed',
  quotes_strip: 'quotesStripEmbed',
}

const NODE_TYPE_TO_EMBED_KIND: Record<string, ArtifactEmbedKind> = {
  plotEmbed: 'plot',
  quoteEmbed: 'quote',
  quotesStripEmbed: 'quotes_strip',
}

export function formatEmbedToken(kind: ArtifactEmbedKind, id: string): string {
  return `{{${kind}:${id}}}`
}

/**
 * Split an artifact MD blob into prose and atomic embed tokens.
 * Unknown `{{…}}` forms stay in text segments.
 */
export function parseEmbedTokens(markdown: string): Array<ArtifactSegment> {
  const segments: Array<ArtifactSegment> = []
  const tokenRe = new RegExp(EMBED_TOKEN_RE.source, 'g')
  let lastIndex = 0
  let match = tokenRe.exec(markdown)

  while (match) {
    if (match.index > lastIndex) {
      segments.push({
        kind: 'text',
        value: markdown.slice(lastIndex, match.index),
      })
    }

    segments.push({
      kind: match[1] as ArtifactEmbedKind,
      id: match[2],
    })
    lastIndex = match.index + match[0].length
    match = tokenRe.exec(markdown)
  }

  if (lastIndex < markdown.length) {
    segments.push({ kind: 'text', value: markdown.slice(lastIndex) })
  }

  if (segments.length === 0) {
    return [{ kind: 'text', value: markdown }]
  }

  return segments
}

export function serializeEmbedTokens(segments: Array<ArtifactSegment>): string {
  return segments
    .map((segment) => {
      if (segment.kind === 'text') {
        return segment.value
      }

      return formatEmbedToken(segment.kind, segment.id)
    })
    .join('')
}

export function segmentsToEditorDoc(
  segments: Array<ArtifactSegment>,
): ArtifactEditorDoc {
  const content: Array<ArtifactEditorNode> = []

  segments.forEach((segment) => {
    if (segment.kind !== 'text') {
      content.push({
        type: EMBED_NODE_TYPE[segment.kind],
        attrs: { embedId: segment.id },
      })
      return
    }

    if (segment.value.length === 0) {
      return
    }

    content.push(textSegmentToParagraph(segment.value))
  })

  if (content.length === 0) {
    return { type: 'doc', content: [{ type: 'paragraph' }] }
  }

  return { type: 'doc', content }
}

export function editorDocToMarkdown(doc: ArtifactEditorDoc): string {
  const nodes = doc.content ?? []
  let markdown = ''

  nodes.forEach((node, index) => {
    const embedKind = node.type ? NODE_TYPE_TO_EMBED_KIND[node.type] : undefined
    if (embedKind) {
      markdown += formatEmbedToken(embedKind, String(node.attrs?.embedId ?? ''))
      return
    }

    markdown += blockNodeToText(node)

    const next = nodes[index + 1]
    if (next && !isEmbedNode(node) && !isEmbedNode(next)) {
      markdown += '\n\n'
    }
  })

  return markdown
}

function isEmbedNode(node: ArtifactEditorNode): boolean {
  return Boolean(node.type && NODE_TYPE_TO_EMBED_KIND[node.type])
}

function textSegmentToParagraph(value: string): ArtifactEditorNode {
  const lines = value.split('\n')
  const content: Array<ArtifactEditorNode> = []

  lines.forEach((line, index) => {
    if (line.length > 0) {
      content.push({ type: 'text', text: line })
    }
    if (index < lines.length - 1) {
      content.push({ type: 'hardBreak' })
    }
  })

  return {
    type: 'paragraph',
    content: content.length > 0 ? content : undefined,
  }
}

function blockNodeToText(node: ArtifactEditorNode): string {
  if (node.type === 'heading') {
    const level = Number(node.attrs?.level ?? 1)
    const hashes = '#'.repeat(Math.min(Math.max(level, 1), 6))
    const headingText = inlineNodeToText(node)
    return headingText ? `${hashes} ${headingText}` : hashes
  }

  if (node.type === 'bulletList') {
    return (node.content ?? [])
      .map((item) => `- ${inlineNodeToText(item).trim()}`)
      .join('\n')
  }

  if (node.type === 'orderedList') {
    return (node.content ?? [])
      .map((item, itemIndex) => `${itemIndex + 1}. ${inlineNodeToText(item).trim()}`)
      .join('\n')
  }

  if (node.type === 'blockquote') {
    const inner = (node.content ?? []).map((child) => blockNodeToText(child)).join('\n')
    return inner
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n')
  }

  return inlineNodeToText(node)
}

function inlineNodeToText(node: ArtifactEditorNode): string {
  if (node.type === 'text') {
    return applyTextMarks(node.text ?? '', node.marks)
  }

  if (node.type === 'hardBreak') {
    return '\n'
  }

  return (node.content ?? []).map((child) => inlineNodeToText(child)).join('')
}

function applyTextMarks(
  text: string,
  marks: Array<{ type?: string }> | undefined,
): string {
  if (!marks || marks.length === 0) {
    return text
  }

  let next = text
  if (marks.some((mark) => mark.type === 'bold')) {
    next = `**${next}**`
  }
  if (marks.some((mark) => mark.type === 'italic')) {
    next = `*${next}*`
  }
  return next
}
